import { useNavigate } from 'react-router-dom'
import type { Medication } from '../../store/medicationsSlice'

interface MedicationListTableProps {
  medications: Medication[]
}

function getStockStatus(qty: number, threshold: number): 'In Stock' | 'Low Stock' | 'Out of Stock' {
  if (qty === 0) return 'Out of Stock'
  if (qty <= threshold) return 'Low Stock'
  return 'In Stock'
}

const statusStyles: Record<string, string> = {
  'In Stock': 'bg-accent-subtle-bg text-status-success',
  'Low Stock': 'bg-[#f5ecd8] text-status-warning',
  'Out of Stock': 'bg-[#f5dede] text-status-error',
}

const rowHighlight: Record<string, string> = {
  'Low Stock': 'bg-[#fdf8ef]',
  'Out of Stock': 'bg-[#fdf2f2]',
}

const categoryColors: Record<string, string> = {
  Antibiotic: 'bg-[#ddeee3] text-[#3e694e]',
  'Anti-inflammatory': 'bg-[#e8e0d0] text-[#8a6b30]',
  Analgesic: 'bg-[#e0e4e8] text-[#5a6a8a]',
  Sedative: 'bg-[#e8ddf0] text-[#7a5a8a]',
  Vaccine: 'bg-[#d8eaf0] text-[#3a6a7a]',
  Parasiticide: 'bg-[#e5e0d8] text-[#6a6050]',
  Hormone: 'bg-[#f0e0e8] text-[#8a4a6a]',
  Other: 'bg-[#e8e6e2] text-text-muted',
}

export function MedicationListTable({ medications }: MedicationListTableProps) {
  const navigate = useNavigate()

  if (medications.length === 0) {
    return (
      <div data-testid="medications-table" className="flex flex-col">
        <p className="text-[12px] text-text-muted py-8 text-center">No medications found</p>
      </div>
    )
  }

  return (
    <div data-testid="medications-table" className="flex flex-col">
      {/* Header row */}
      <div
        className="med-table-header grid items-center h-9 text-[11px] font-medium text-text-muted uppercase tracking-wide"
        style={{
          gridTemplateColumns: '1.5fr 120px 100px 100px 90px 90px 80px 100px',
        }}
      >
        <span className="px-2">Medication Name</span>
        <span className="px-2">Category</span>
        <span className="px-2">Form</span>
        <span className="px-2">Concentration</span>
        <span className="px-2">Stock Qty</span>
        <span className="px-2">Reorder</span>
        <span className="px-2">Controlled</span>
        <span className="px-2">Status</span>
      </div>

      {/* Data rows */}
      {medications.map((med) => {
        const status = getStockStatus(med.stock_quantity, med.reorder_threshold)
        const highlight = rowHighlight[status] || ''

        return (
          <div
            key={med.id}
            data-testid={`medication-row-${med.id}`}
            onClick={() => navigate(`/medications/${med.id}`)}
            className={`med-table-row grid items-center h-[44px] text-[13px] text-text-primary hover:bg-hover transition-colors duration-100 cursor-pointer ${highlight}`}
            style={{
              gridTemplateColumns: '1.5fr 120px 100px 100px 90px 90px 80px 100px',
            }}
          >
            <span className="px-2 font-[500] truncate" data-testid="medication-name">
              {med.name}
            </span>
            <span className="px-2" data-testid="medication-category">
              <span
                className={`inline-flex items-center h-5 px-1.5 rounded-[3px] text-[11px] font-medium ${categoryColors[med.category] || 'bg-border text-text-muted'}`}
              >
                {med.category}
              </span>
            </span>
            <span className="px-2 text-[12px] text-text-secondary truncate" data-testid="medication-form">
              {med.form}
            </span>
            <span className="px-2 text-[12px] text-text-muted truncate" data-testid="medication-concentration">
              {med.concentration}
            </span>
            <span className="px-2 text-[12px] text-text-secondary" data-testid="medication-stock-qty">
              {med.stock_quantity}
            </span>
            <span className="px-2 text-[12px] text-text-muted" data-testid="medication-reorder">
              {med.reorder_threshold}
            </span>
            <span className="px-2" data-testid="medication-controlled">
              <span
                className={`inline-flex items-center h-5 px-1.5 rounded-[3px] text-[11px] font-medium ${
                  med.controlled_substance
                    ? 'bg-[#f5dede] text-status-error'
                    : 'bg-[#e8e6e2] text-text-muted'
                }`}
              >
                {med.controlled_substance ? 'Yes' : 'No'}
              </span>
            </span>
            <span className="px-2" data-testid="medication-status">
              <span
                className={`inline-flex items-center h-5 px-1.5 rounded-[3px] text-[11px] font-medium ${statusStyles[status]}`}
              >
                {status}
              </span>
            </span>
          </div>
        )
      })}
    </div>
  )
}
